'use client';

import React, { useEffect, useState, useCallback } from 'react';
import {
  Table,
  Tag,
  Typography,
  Row,
  Col,
  Space,
  Button,
  Input,
  Select,
} from 'antd';
import {
  DatabaseOutlined,
  HistoryOutlined,
  AppstoreOutlined,
  ReloadOutlined,
  InboxOutlined,
  EyeOutlined,
  BarcodeOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useRouter } from 'next/navigation';
import {
  ItemStockSummaryRecord,
  InventoryCostLayerRecord,
  InventoryTransactionRecord,
} from '@/lib/mockGrStore';

const { Title, Text } = Typography;

export default function InventoryPage() {
  const router = useRouter();

  const [loading, setLoading] = useState(true);

  const [costLayers, setCostLayers] = useState<InventoryCostLayerRecord[]>([]);
  const [transactionsCount, setTransactionsCount] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [conditionFilter, setConditionFilter] = useState('ALL');

  const fetchInventoryData = useCallback(async () => {
    try {
      setLoading(true);
      const [clRes, txRes] = await Promise.all([
        fetch('/api/inventory/cost-layers'),
        fetch('/api/inventory/transactions'),
      ]);

      const clData = await clRes.json();
      const txData = await txRes.json();

      if (clData.success) setCostLayers(clData.data);
      if (txData.success) setTransactionsCount(txData.data.length);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let ignore = false;
    async function load() {
      try {
        const [clRes, txRes] = await Promise.all([
          fetch('/api/inventory/cost-layers'),
          fetch('/api/inventory/transactions'),
        ]);

        const clData = await clRes.json();
        const txData = await txRes.json();

        if (!ignore) {
          if (clData.success) setCostLayers(clData.data);
          if (txData.success) setTransactionsCount(txData.data.length);
        }
      } catch {
        // ignore
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => {
      ignore = true;
    };
  }, []);

  // Overall Inventory Stats
  const totalStockValuation = costLayers.reduce((sum, item) => sum + item.remainingQty * item.unitCost, 0);
  const totalLotsCount = costLayers.length;
  const uniqueItemsCount = new Set(costLayers.map((c) => c.itemId)).size;

  // Filter Inventory Items (Lots)
  const filteredInventoryItems = costLayers.filter((l) => {
    const q = searchQuery.toLowerCase();
    const invId = (l.inventoryId || l.id).toLowerCase();
    const itemMatch =
      invId.includes(q) ||
      l.itemCode.toLowerCase().includes(q) ||
      l.itemName.toLowerCase().includes(q) ||
      l.grNumber.toLowerCase().includes(q) ||
      l.poNumber.toLowerCase().includes(q) ||
      (l.batchNumber && l.batchNumber.toLowerCase().includes(q)) ||
      (l.warehouseLocation && l.warehouseLocation.toLowerCase().includes(q)) ||
      (l.condition && l.condition.toLowerCase().includes(q));

    if (!itemMatch) return false;

    if (conditionFilter === 'GRADE_A') {
      return l.condition?.includes('Grade A') || !l.condition?.includes('Grade B');
    }
    if (conditionFilter === 'GRADE_B') {
      return l.condition?.includes('Grade B') || l.condition?.includes('Defect') || l.condition?.includes('Khusus');
    }

    return true;
  });

  // Table columns for Tab 1: Physical Inventory Items by Unique Inventory ID
  const inventoryItemsColumns = [
    {
      title: 'Inventory ID',
      key: 'inventoryId',
      width: 170,
      render: (_: unknown, r: InventoryCostLayerRecord) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Tag
            color="geekblue"
            onClick={() => router.push(`/inventory/${encodeURIComponent(r.inventoryId || r.id)}`)}
            style={{
              cursor: 'pointer',
              fontWeight: 700,
              borderRadius: 6,
              padding: '2px 8px',
              fontSize: 12,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              width: 'fit-content',
            }}
          >
            <BarcodeOutlined />
            {r.inventoryId || r.id}
          </Tag>
          <span style={{ fontSize: 11, color: '#94A3B8' }}>
            {new Date(r.createdAt).toLocaleDateString('id-ID', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })}
          </span>
        </div>
      ),
    },
    {
      title: 'Item Katalog',
      key: 'item',
      render: (_: unknown, r: InventoryCostLayerRecord) => (
        <div>
          <div style={{ fontWeight: 700, color: '#0F172A', fontSize: 13 }}>{r.itemName}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
            <span style={{ color: '#6366F1', fontWeight: 600, fontSize: 12 }}>{r.itemCode}</span>
            <span style={{ color: '#CBD5E1' }}>•</span>
            <Tag color="purple" style={{ fontSize: 10, borderRadius: 4, margin: 0 }}>
              {r.category || 'Packaging & Warehouse'}
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: 'Kondisi & Mutu Fisik',
      key: 'condition',
      width: 220,
      render: (_: unknown, r: InventoryCostLayerRecord) => {
        const isDefect =
          r.condition?.toLowerCase().includes('defect') ||
          r.condition?.toLowerCase().includes('retak') ||
          r.condition?.toLowerCase().includes('khusus');

        return (
          <div>
            <Tag
              color={isDefect ? 'orange' : 'green'}
              style={{ fontWeight: 600, borderRadius: 6, fontSize: 11, marginBottom: 2 }}
            >
              {r.condition || 'Grade A (QC Passed)'}
            </Tag>
            {r.conditionNotes && (
              <div
                style={{
                  fontSize: 11,
                  color: '#64748B',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 200,
                }}
                title={r.conditionNotes}
              >
                {r.conditionNotes}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Batch & Lokasi Gudang',
      key: 'location',
      width: 190,
      render: (_: unknown, r: InventoryCostLayerRecord) => (
        <div style={{ fontSize: 12 }}>
          <div style={{ fontWeight: 600, color: '#1E293B' }}>
            {r.warehouseLocation || 'Gudang Utama - Area A'}
          </div>
          <div style={{ color: '#64748B', fontSize: 11 }}>
            Batch: <strong>{r.batchNumber || '-'}</strong>
          </div>
        </div>
      ),
    },
    {
      title: 'Stok On-Hand',
      dataIndex: 'remainingQty',
      key: 'remainingQty',
      align: 'right' as const,
      width: 130,
      render: (qty: number, r: InventoryCostLayerRecord) => (
        <Tag
          color="cyan"
          style={{
            fontWeight: 700,
            fontSize: 12,
            padding: '2px 8px',
            borderRadius: 6,
          }}
        >
          {qty.toLocaleString('id-ID')} {r.uomCode}
        </Tag>
      ),
    },
    {
      title: 'Harga Beli (PO)',
      dataIndex: 'unitCost',
      key: 'unitCost',
      align: 'right' as const,
      width: 130,
      render: (cost: number) => (
        <span style={{ color: '#475569', fontSize: 12 }}>Rp {cost.toLocaleString('id-ID')}</span>
      ),
    },
    {
      title: 'Total Valuasi Lot',
      key: 'totalValue',
      align: 'right' as const,
      width: 140,
      render: (_: unknown, r: InventoryCostLayerRecord) => (
        <strong style={{ color: '#0F172A', fontSize: 13 }}>
          Rp {(r.remainingQty * r.unitCost).toLocaleString('id-ID')}
        </strong>
      ),
    },
    {
      title: 'Asal Dokumen',
      key: 'source',
      width: 150,
      render: (_: unknown, r: InventoryCostLayerRecord) => (
        <div style={{ fontSize: 11, color: '#64748B' }}>
          <div>
            GR: <strong style={{ color: '#0284C7' }}>{r.grNumber}</strong>
          </div>
          <div>
            PO: <strong style={{ color: '#7C3AED' }}>{r.poNumber}</strong>
          </div>
        </div>
      ),
    },
    {
      title: 'Aksi',
      key: 'action',
      width: 110,
      align: 'center' as const,
      render: (_: unknown, r: InventoryCostLayerRecord) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => router.push(`/inventory/${encodeURIComponent(r.inventoryId || r.id)}`)}
          style={{
            borderRadius: 8,
            background: '#6366F1',
            fontWeight: 600,
            fontSize: 12,
          }}
        >
          Detail
        </Button>
      ),
    },
  ];

  return (
    <div style={{ width: '100%', paddingBottom: 40 }}>
      {/* Page Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <Title level={3} style={{ margin: 0, color: '#0F172A', fontWeight: 800 }}>
            Manajemen Stok Inventaris Fisik
          </Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Setiap item di inventaris memiliki ID Inventaris unik dengan spesifikasi kondisi, nomor batch, dan lokasi penyimpanan independen.
          </Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchInventoryData} style={{ borderRadius: 10 }}>
            Segarkan
          </Button>
          <Button
            type="primary"
            icon={<InboxOutlined />}
            onClick={() => router.push('/goods-receipts/receive')}
            style={{
              borderRadius: 10,
              background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
              fontWeight: 600,
            }}
          >
            Terima Barang (GR)
          </Button>
        </Space>
      </div>

      {/* KPI Pastel Stat Cards */}
      <Row gutter={[10, 10]} style={{ marginBottom: 20 }}>
        <Col xs={12} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #EEF2FF 100%)',
              padding: '12px 14px',
              borderRadius: 14,
              border: '1px solid #E0E7FF',
              boxShadow: '0 4px 16px -2px rgba(99, 102, 241, 0.05)',
            }}
          >
            <div style={{ fontSize: 10.5, fontWeight: 700, color: '#4F46E5', textTransform: 'uppercase' }}>
              Valuasi Persediaan
            </div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#1E1B4B', marginTop: 2, wordBreak: 'break-word' }}>
              Rp {totalStockValuation.toLocaleString('id-ID')}
            </div>
            <div style={{ fontSize: 10.5, color: '#64748B', marginTop: 2 }}>FIFO cost layer</div>
          </div>
        </Col>

        <Col xs={12} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F0FDF4 100%)',
              padding: '12px 14px',
              borderRadius: 14,
              border: '1px solid #BBF7D0',
              boxShadow: '0 4px 16px -2px rgba(16, 185, 129, 0.05)',
            }}
          >
            <div style={{ fontSize: 10.5, fontWeight: 700, color: '#15803D', textTransform: 'uppercase' }}>
              Lot Aktif
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#064E3B', marginTop: 2 }}>
              {totalLotsCount}{' '}
              <span style={{ fontSize: 11, fontWeight: 600, color: '#15803D' }}>Lot</span>
            </div>
            <div style={{ fontSize: 10.5, color: '#64748B', marginTop: 2 }}>ID Lot unik</div>
          </div>
        </Col>

        <Col xs={12} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #FFFBEB 100%)',
              padding: '12px 14px',
              borderRadius: 14,
              border: '1px solid #FDE68A',
              boxShadow: '0 4px 16px -2px rgba(245, 158, 11, 0.05)',
            }}
          >
            <div style={{ fontSize: 10.5, fontWeight: 700, color: '#B45309', textTransform: 'uppercase' }}>
              Varian Katalog
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#78350F', marginTop: 2 }}>
              {uniqueItemsCount}{' '}
              <span style={{ fontSize: 11, fontWeight: 600, color: '#B45309' }}>Jenis</span>
            </div>
            <div style={{ fontSize: 10.5, color: '#64748B', marginTop: 2 }}>Katalog barang</div>
          </div>
        </Col>

        <Col xs={12} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F0F9FF 100%)',
              padding: '12px 14px',
              borderRadius: 14,
              border: '1px solid #BAE6FD',
              boxShadow: '0 4px 16px -2px rgba(14, 165, 233, 0.05)',
            }}
          >
            <div style={{ fontSize: 10.5, fontWeight: 700, color: '#0369A1', textTransform: 'uppercase' }}>
              Log Mutasi
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#0C4A6E', marginTop: 2 }}>
              {transactionsCount}
            </div>
            <div style={{ fontSize: 10.5, color: '#64748B', marginTop: 2 }}>Barang masuk</div>
          </div>
        </Col>
      </Row>

      {/* Tabs & Search Filter Container */}
      <div
        style={{
          background: '#FFFFFF',
          borderRadius: 16,
          border: '1px solid #F1F5F9',
          boxShadow: '0 4px 20px -2px rgba(99, 102, 241, 0.04)',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #F1F5F9' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 14,
              flexWrap: 'wrap',
              gap: 10,
            }}
          >
            <span style={{ fontWeight: 700, fontSize: 15, color: '#0F172A' }}>
              <DatabaseOutlined style={{ color: '#6366F1', marginRight: 8 }} />
              Daftar Unit Stok Fisik ({costLayers.length} Lot Aktif)
            </span>

            <Space size="small">
              <Button
                size="small"
                icon={<HistoryOutlined />}
                onClick={() => router.push('/inventory/transactions')}
                style={{ borderRadius: 8, fontSize: 12, color: '#0369A1', borderColor: '#BAE6FD' }}
              >
                Buku Mutasi Transaksi ➔
              </Button>
              <Button
                size="small"
                icon={<AppstoreOutlined />}
                onClick={() => router.push('/inventory/catalog-summary')}
                style={{ borderRadius: 8, fontSize: 12, color: '#4338CA', borderColor: '#C7D2FE' }}
              >
                Ringkasan Master Katalog ➔
              </Button>
            </Space>
          </div>

          {/* Search & Condition Filter Bar */}
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={16} md={14}>
              <Input
                placeholder="Cari berdasarkan ID Inventaris (INV-...), Kode, Nama Barang, Batch, atau Lokasi..."
                prefix={<SearchOutlined style={{ color: '#94A3B8' }} />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                allowClear
                style={{ borderRadius: 10 }}
              />
            </Col>
            <Col xs={24} sm={8} md={6}>
              <Select
                value={conditionFilter}
                onChange={setConditionFilter}
                style={{ width: '100%', borderRadius: 10 }}
                options={[
                  { value: 'ALL', label: 'Semua Kondisi Fisik' },
                  { value: 'GRADE_A', label: 'Grade A (Sempurna)' },
                  { value: 'GRADE_B', label: 'Grade B (Kondisi Khusus / Defect)' },
                ]}
              />
            </Col>
          </Row>
        </div>

        {/* Table: Physical Inventory Items by Unique ID */}
        <Table
          rowKey="id"
          columns={inventoryItemsColumns}
          dataSource={filteredInventoryItems}
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          size="middle"
          scroll={{ x: 1000 }}
        />
      </div>
    </div>
  );
}
